import { Typography, useTheme } from "@mui/material";
import { TooltipTrigger } from "components/Tooltip/styled";
import { useTooltips } from "hooks/useTooltips";
import { Row } from "v2/components/base/Flex/ui";
import { MainPageLayout } from "v2/layouts/MainPageLayout";
import HelpIcon from "@mui/icons-material/Help";
import { DiscoverLayout } from "v2/layouts/DiscoverLayout";
import Scrollable from "v2/components/base/Scrollable/ui";
import { useRef, useState } from "react";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import { useInfiniteQuery } from "@tanstack/react-query";
import axiosService from "services/axios";
import { useInViewInfiniteQuery } from "hooks/useInViewInfiniteQuery";
import { useParams } from "react-router-dom";
import GroupRowItem from "v2/components/ScrollableItem/GroupRowItem/ui";
import DefinitionTooltip from "v2/components/Tooltip/ui";
import { useCheckViewPoint } from "hooks/useCheckViewPoint";
import { VIEW_POINT_CONFIG } from "hooks/useCheckViewPoint";
import { PROJECT_DISCOVER_CONFIGS } from "consts";

export const AirdropGroup = () => {
  const theme = useTheme();
  const { projectId } = useParams();
  const { title, tooltip } =
    PROJECT_DISCOVER_CONFIGS[projectId as keyof typeof PROJECT_DISCOVER_CONFIGS];

  /* User */
  const { tgUserId, walletAddresses = {} } = useUserStoreV2();
  const { tonAddress } = walletAddresses;
  const [page, setPage] = useState(1);

  const scrollableRef = useRef<HTMLDivElement>(null);

  const queryResult = useInfiniteQuery({
    queryKey: [
      `${projectId}-groups`,
      {
        page,
        mode: "all",
        name: "",
        projectId: projectId,
      },
    ],
    queryFn: async ({ pageParam }) => {
      return axiosService.getProjectList({
        projectId: projectId as string,
      });
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage.page + 1;
    },

    getPreviousPageParam: (firstPage, pages) => {
      return firstPage.page - 1;
    },
    initialPageParam: 1,
  });

  const { data, isLoading, isSuccess } = queryResult;

  const { ref, hasAllFetched } = useInViewInfiniteQuery(queryResult);

  return (
    <>
      <MainPageLayout
        Title={
          <Header
            title={title}
            tooltipText={tooltip.description}
            toolTipHeader={tooltip.title ?? title}
          />
        }>
        <DiscoverLayout
          Filters={<></>}
          Scrollable={
            <>
              <Scrollable
                ref={scrollableRef}
                isLoading={isLoading}
                isEmpty={!data?.pages[0].length || data?.pages[0].totalResults === 0}
                emptyProps={{
                  title: "Calculating SHILL…",
                  description: "Come back the next day to check your position!",
                }}
                hasAllFetched={hasAllFetched}
                loadMoreRef={ref}>
                {data?.pages
                  .flatMap((page) => page)
                  .map((group: any, index: number) => {
                    return (
                      <Row key={group.tgGroupId} sx={{ pointerEvents: "none" }} fullWidth>
                        <GroupRowItem
                          cabal={{
                            tgGroupId: group.tgGroupId,
                            imageUri: group.imageUri,
                            name: group.name,
                            change: group.dailyShillsChange,
                            shills: group.totalShills,
                          }}
                          rank={index + 1}
                        />
                      </Row>
                    );
                  })}
              </Scrollable>
            </>
          }
        />
      </MainPageLayout>
    </>
  );
};
const Header = ({
  title,
  toolTipHeader,
  tooltipText,
}: {
  title: string;
  toolTipHeader?: string;
  tooltipText: string | React.ReactNode;
}) => {
  const { isTooltipOpen, setIsTooltipOpen, tooltipRef } = useTooltips();

  const isMobile = useCheckViewPoint(VIEW_POINT_CONFIG.MOBILE);

  const handleTriggerClick = () => {
    setIsTooltipOpen(!isTooltipOpen);
  };

  return (
    <>
      {/* Header */}
      <Row type="sc" sx={{ gap: "4px", whiteSpace: "nowrap" }}>
        <Typography variant={isMobile ? "mobile-h3" : "mobile-h2"}>{title}</Typography>
        <TooltipTrigger ref={tooltipRef} isOpen={isTooltipOpen} onClick={handleTriggerClick}>
          <Row sx={{ position: "absolute", top: -14, left: "50%" }}>
            <HelpIcon sx={{ width: "28px", height: "28px" }} />
          </Row>
        </TooltipTrigger>
      </Row>
      {/* Tooltip Modal */}
      <DefinitionTooltip term={toolTipHeader ?? title} isShow={isTooltipOpen} maxWidth="480px">
        <Typography variant="body-default">{tooltipText}</Typography>
      </DefinitionTooltip>
    </>
  );
};
